<template>
  <div v-show="toggle">
    <div v-if="isAdmin" v-bind="props" @mouseleave="hoverProcess"
      class="fixed top-0 w-full bg-neutral-800 h-410 !z-[1100] flex flex-wrap gap-2 p-4">
      <div class="flex flex-wrap gap-2">
        <v-btn @click="clickCommute" class="w-32 !h-32 " color="green">
          <div class="block">
            <v-icon class="!text-8xl col-12 !h-20" icon="far fa-check-circle" />
            <div class="flex justify-center w-full text-sm">{{(!items || (items && items.length < 1)) ? '출근' : `퇴근 ${endTime} 가능`}}</div>
          </div>
        </v-btn>
        <v-btn class="w-32 !h-32" color="purple" :to="`/calendar/1`">
          <div class="block">
            <v-icon class="!text-8xl col-12 !h-20" icon="far fa-calendar" />
            <!-- 개인 캘린더 -->
            <div class="flex justify-center w-full text-sm">개인 캘린더</div>
          </div>
        </v-btn>
        <!-- 2026-04-28 belongYear 강제 지정 제거 — 상세 페이지가 입사일 기준 자동 계산하도록 위임 (지급일 이전이면 전년도) -->
        <v-btn class="w-32 !h-32" color="red" :to="`/holidayManage/${userStore.id}`">
          <div class="block">
            <v-icon class="!text-8xl col-12 !h-20" icon="far fa-smile" />
            <!-- 개인 연차 -->
            <div class="flex justify-center w-full text-sm">개인연차</div>
          </div>
        </v-btn>
        <v-btn class="w-32 !h-32" color="yellow" :to="`/commuteRecord/${userStore.id}?year=${dayjs().format('YYYY')}&month=${dayjs().format('M')}`">
          <div class="block">
            <v-icon class="!text-8xl col-12 !h-20" icon="far fa-calendar-lines" />
            <!-- 출퇴근 기록 -->
            <div class="flex justify-center w-full text-sm">출퇴근기록</div>
          </div>
        </v-btn>
        <v-btn class="w-32 !h-32" color="blue" to="/email/inbox">
          <div class="block">
            <v-icon class="!text-8xl col-12 !h-20" icon="far fa-mailbox" />
            <!-- 내 메일함 -->
            <div class="flex justify-center w-full text-sm">메일함</div>
          </div>
        </v-btn>
        <!-- 2026-10-29 도약 프로그램 인스톨러 받기 — 주소를 외우지 않아도 되게.
             받는 주소는 사내 IP 전용이라 회사 밖에선 막힌다 → 새 탭으로 열어 실패 화면이 관리자 화면을 덮지 않게 -->
        <v-btn class="w-32 !h-32" color="teal" :href="INSTALLER_URL" target="_blank" rel="noopener"
          title="도약 프로그램 인스톨러 받기 (회사 인터넷에서만 받을 수 있습니다)">
          <div class="block">
            <v-icon class="!text-8xl col-12 !h-20" icon="far fa-laptop-arrow-down" />
            <div class="flex justify-center w-full text-sm">도약 인스톨러</div>
          </div>
        </v-btn>
      </div>
    </div>

    <DYACommuteRecordModal v-model:modal-toggle="modalToggle" :records="items" @request-fetch="requestApi"></DYACommuteRecordModal>
  </div>
</template>

<script setup lang='ts'>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCalendar, faCalendarLines, faCheckCircle, faLaptopArrowDown, faMailbox, faSmile } from '@fortawesome/pro-regular-svg-icons';
import dayjs from 'dayjs';
import { useDYComponent } from '~/modules/nuxt3-module-doyac/composables/dy-component';
import { extractDataOrFailure, useDYFetchWithToken } from '~/modules/nuxt3-module-doyac/composables/fetch';
import { useDYUserStore } from '~/modules/nuxt3-module-doyac/stores/user';
import DYACommuteRecordModal from './modal/DYACommuteRecordModal.vue';
import { notify } from '@kyvg/vue3-notification';
import { isArray } from 'lodash-es';
import { useDYAuth } from '~/modules/nuxt3-module-doyac/composables/auth';
import { useDYAppConfig } from '~/modules/nuxt3-module-doyac/composables/config';
library.add(faCheckCircle, faCalendar, faSmile, faCalendarLines, faMailbox, faLaptopArrowDown)

// 도약 프로그램 인스톨러 최신 exe (python_apps/doyac_installer) — 받으면 「도약 프로그램 인스톨러.exe」 로 저장된다
const INSTALLER_URL = 'https://apps.doyacstorage.com/doyac_installer/doyac_installer.exe'

const dyComponent = useDYComponent()

const props = withDefaults(defineProps<{
  toggle: boolean
}>(), {
  toggle: () => false,
})

const emit = defineEmits<{
  (e: 'update:toggle', v: boolean): void
}>()

const userStore = useDYUserStore()
const { isCEO, isTeacher, isPartner, isAdmin } = useDYAuth()
const { API_BASE_URL } = useDYAppConfig()

// ------------------ 인터페이스 ------------------

export interface CommuteRecord {
  no: number
  signature_time: string
  work_time: string
  work_type: string
}

// ------------------ 데이터 ------------------

const items = ref<CommuteRecord[]>()
const modalToggle = ref(false)

// ------------------ 컴퓨티드 ------------------

const endTime = computed(()=>{
  if(!items.value || !items.value[0]) return undefined

  const start = dayjs(items.value[0].work_time)
  return start.add(9,'h').format('HH:mm')
})

// ------------------ 메서드 ------------------

const clickCommute = () => {
  const now = dayjs()
  if(Number(now.format('H')) < 7 ) {
    notify({ type: 'error', text: '출근 체크는 7시 이후부터 가능합니다' })
    return
  }

  if(items.value?.length === 2) {
    notify({ type: 'error', text: '오늘은 이미 출퇴근 체크를 전부 하였습니다' })
    return
  }

  modalToggle.value = true
}

const hoverProcess = ($event: any) => {
  if ($event) emit('update:toggle', false)
}

const requestApi = async () => {
  const response = await useDYFetchWithToken(`${API_BASE_URL}/api/commute-records`, { method: 'get', params: { todayRecord: true, signer: userStore.id }, immediate: false })
  await response.execute({dedupe: 'defer'})
  items.value = extractDataOrFailure(response).items
}

// ------------------ 훅 ------------------

onMounted(async () => {
  await nextTick()

})

dyComponent.$onActivated(async () => {
  await userStore.ready()
  
  await nextTick()
  if(!isCEO.value && isAdmin.value) await requestApi()
  
  if(isAdmin.value && !isCEO.value && !isTeacher.value && !isPartner.value && (!items.value || (items.value && items.value?.length < 1))) { //CEO는 출첵안하도록
    if(userStore.id !== 'system'){
      if(confirm('아직 출근체크를 하지 않았습니다. 출근체크를 하시겠습니까?')) {
        modalToggle.value = true  
      }
    }
  }
})
</script>

<style scoped lang='scss'>
//</style>